import { useState, useCallback, useEffect, useRef } from 'react';
import { Mosaic, MosaicWindow, RemoveButton, getLeaves, type MosaicNode } from 'react-mosaic-component';
import 'react-mosaic-component/react-mosaic-component.css';
import './mosaic-overrides.css';
import { LayoutGrid, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useMosaicLayoutPersistence } from '@/hooks/sip/useMosaicLayoutPersistence';
import { SIPMosaicTileRenderer, type SIPTileProps } from './SIPMosaicTileRenderer';
import { SIPMosaicDragLayer } from './SIPMosaicDragLayer';
import { AddPanelButton } from './AddPanelButton';
import { TILE_TITLES, type TileId } from './types';

const MOBILE_TILE_ORDER: TileId[] = ['map', 'at-a-glance', 'timeline', 'evolution', 'uncertainty'];

function removeTileFromTree(tree: MosaicNode<TileId>, tileId: TileId): MosaicNode<TileId> | null {
  if (tree === tileId) return null;
  if (typeof tree === 'string') return tree;

  const first = removeTileFromTree(tree.first, tileId);
  const second = removeTileFromTree(tree.second, tileId);

  if (!first) return second;
  if (!second) return first;

  return {
    ...tree,
    first,
    second,
  };
}

type SIPMosaicLayoutProps = Omit<SIPTileProps, 'mapInteraction' | 'filters' | 'leafHintVisible'> & {
  mapInteraction: SIPTileProps['mapInteraction'];
  filters: SIPTileProps['filters'];
  leafHintVisible?: boolean;
};

export function SIPMosaicLayout(props: SIPMosaicLayoutProps) {
  const isMobile = useIsMobile();
  const { mosaicTree, saveStatus, isDirty, applyTree, handleReset, handleSave } =
    useMosaicLayoutPersistence();

  const [draggingTileId, setDraggingTileId] = useState<TileId | null>(null);
  const [ghostRect, setGhostRect] = useState<DOMRect | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const isDraggingRef = useRef(false);
  const frozenTreeRef = useRef<MosaicNode<TileId> | null>(null);
  const pendingTreeRef = useRef<MosaicNode<TileId> | null>(null);

  const handleChange = useCallback((newTree: MosaicNode<TileId> | null) => {
    // While a drag is active, the library calls:
    //   1. `mosaicActions.hide(path)` on drag start (deferred) — sets parent
    //      splitPercentage to 0/100, visually collapsing the origin slot so
    //      siblings reflow into its space. (LeetCode does NOT do this.)
    //   2. `mosaicActions.updateTree(createDragToUpdates(...))` on drop, OR
    //      a splitPercentage reset on cancel.
    // We queue these updates instead of applying them. On drag end we commit
    // only if the drag resulted in a real drop; otherwise we discard and the
    // original tree is preserved unchanged — so the origin slot stays put.
    if (isDraggingRef.current) {
      pendingTreeRef.current = newTree;
      return;
    }
    applyTree(newTree);
  }, [applyTree]);

  const handleDragStart = useCallback((id: TileId) => {
    setDraggingTileId(id);
    isDraggingRef.current = true;
    frozenTreeRef.current = mosaicTree;
    pendingTreeRef.current = null;
    // Capture the origin rect synchronously — runs before the library's
    // deferred hide() call, so the .mosaic-tile for this leaf is still in
    // the DOM with its real geometry.
    const marker = document.querySelector<HTMLElement>(
      `[data-tile-id="${id}"]`,
    );
    const tile = marker?.closest<HTMLElement>('.mosaic-tile');
    if (tile) {
      setGhostRect(tile.getBoundingClientRect());
    }
    // Track cursor via native HTML5 drag events so the custom drag-layer
    // follows the pointer smoothly. These fire on document regardless of
    // react-dnd's provider context.
    const handleDragOver = (e: DragEvent) => {
      setCursor({ x: e.clientX, y: e.clientY });
    };
    document.addEventListener('dragover', handleDragOver);
    dragCleanupRef.current = () => {
      document.removeEventListener('dragover', handleDragOver);
      dragCleanupRef.current = null;
    };
  }, []);

  const handleDragEnd = useCallback((type: 'drop' | 'reset') => {
    isDraggingRef.current = false;
    const pending = pendingTreeRef.current;
    const frozen = frozenTreeRef.current;
    pendingTreeRef.current = null;
    frozenTreeRef.current = null;
    if (type === 'drop' && pending !== null) {
      applyTree(pending);
    } else {
      // Cancel — discard any intermediate hide/reset mutations the library
      // queued. Force a re-apply of the frozen tree to bust any transient
      // internal splitPercentage state the library may have set on its way.
      applyTree(frozen);
    }
    setDraggingTileId(null);
    setGhostRect(null);
    setCursor(null);
    dragCleanupRef.current?.();
  }, [applyTree]);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const tileProps: SIPTileProps = {
    tableData: props.tableData,
    aggregationRow: props.aggregationRow,
    availableVariables: props.availableVariables,
    selectedVariables: props.selectedVariables,
    onSelectedVariablesChange: props.onSelectedVariablesChange,
    csvMetadata: props.csvMetadata,
    tableLoading: props.tableLoading,
    bestEstimateVariableUnits: props.bestEstimateVariableUnits,
    effectiveVariableUnits: props.effectiveVariableUnits,
    nodeId: props.nodeId,
    modelId: props.modelId,
    weatherModelId: props.weatherModelId,
    startDate: props.startDate,
    forecastRange: props.forecastRange,
    run: props.run,
    availableRuns: props.availableRuns,
    availableRunsLoading: props.availableRunsLoading,
    evolutionEnabled: props.evolutionEnabled,
    scopeLeafCount: props.scopeLeafCount,
    stormImpactModel: props.stormImpactModel,
    mapInteraction: props.mapInteraction,
    filters: props.filters,
    leafHintVisible: props.leafHintVisible,
  };

  if (isMobile) {
    const openTiles = mosaicTree
      ? getLeaves(mosaicTree).sort(
          (a, b) => MOBILE_TILE_ORDER.indexOf(a) - MOBILE_TILE_ORDER.indexOf(b),
        )
      : [];

    return (
      <div className="relative h-full w-full">
        {openTiles.length > 0 ? (
          <div className="sip-mosaic-mobile-scroll h-full w-full overflow-y-auto">
            {openTiles.map((id) => (
              <section key={id} className="sip-mosaic-mobile-tile mosaic-tile">
                <div className="mosaic-window h-full w-full">
                  <div className="mosaic-window-toolbar">
                    <div className="mosaic-window-title">{TILE_TITLES[id]}</div>
                    <div className="mosaic-window-controls">
                      <button
                        type="button"
                        title="Maximize"
                        aria-label="Maximize"
                        className="mosaic-default-control expand-button"
                        onClick={() => applyTree(id)}
                      />
                      <button
                        type="button"
                        title="Remove"
                        aria-label="Remove"
                        className="mosaic-default-control close-button"
                        onClick={() => applyTree(removeTileFromTree(mosaicTree!, id))}
                      />
                    </div>
                  </div>
                  <div className="mosaic-window-body">
                    <div data-tile-id={id} className="h-full w-full">
                      <SIPMosaicTileRenderer id={id} tileProps={tileProps} />
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center p-6">
            <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-border bg-card px-8 py-10 text-center shadow-sm">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <LayoutGrid className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">
                  All panels minimized
                </h3>
                <p className="text-sm text-muted-foreground">
                  You've closed every panel. Add one back with the Panels menu,
                  or restore the default layout to get started.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset Layout
                </Button>
              </div>
            </div>
          </div>
        )}
        <AddPanelButton
          mosaicTree={mosaicTree}
          onTreeChange={handleChange}
          onReset={handleReset}
          onSave={handleSave}
          saveStatus={saveStatus}
          isDirty={isDirty}
        />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <Mosaic<TileId>
        renderTile={(id, path) => (
          <MosaicWindow<TileId>
            path={path}
            title={TILE_TITLES[id]}
            createNode={() => id}
            toolbarControls={
              <>
                <button
                  type="button"
                  title="Maximize"
                  aria-label="Maximize"
                  className="mosaic-default-control expand-button"
                  onClick={() => applyTree(id)}
                />
                <RemoveButton />
              </>
            }
            onDragStart={() => handleDragStart(id)}
            onDragEnd={handleDragEnd}
          >
            <div data-tile-id={id} className="h-full w-full">
              <SIPMosaicTileRenderer id={id} tileProps={tileProps} />
            </div>
          </MosaicWindow>
        )}
        value={mosaicTree}
        onChange={handleChange}
        className="h-full"
        zeroStateView={
          <div className="flex h-full w-full items-center justify-center p-6">
            <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-border bg-card px-8 py-10 text-center shadow-sm">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <LayoutGrid className="h-7 w-7" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-foreground">
                  All panels minimized
                </h3>
                <p className="text-sm text-muted-foreground">
                  You've closed every panel. Add one back with the Panels menu,
                  or restore the default layout to get started.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReset}
                  className="gap-1.5"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset Layout
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Tip: use the Panels button in the bottom-right to add a single panel.
              </p>
            </div>
          </div>
        }
      />
      <AddPanelButton
        mosaicTree={mosaicTree}
        onTreeChange={handleChange}
        onReset={handleReset}
        onSave={handleSave}
        saveStatus={saveStatus}
        isDirty={isDirty}
      />
      {ghostRect && (
        <div
          className="sip-mosaic-ghost"
          style={{
            top: ghostRect.top,
            left: ghostRect.left,
            width: ghostRect.width,
            height: ghostRect.height,
          }}
        />
      )}
      <SIPMosaicDragLayer
        tileTitle={draggingTileId ? TILE_TITLES[draggingTileId] : null}
        cursor={cursor}
      />
    </div>
  );
}
